// Chapter navigation rail — desktop only.
//
// Vertical stack of Roman numerals I–V, fixed to the right edge.
// The numeral matching the active chapter glows in accent color.
// Clicking scrolls smoothly to the corresponding section.
//
// Hidden on touch / narrow viewports via the CSS media query in
// src/styles/chapters.css. No need to detect at runtime.

import type { ChapterKey, ChapterChangeDetail } from './chapterState';

const CHAPTERS: Array<{ key: ChapterKey; label: string; aria: string }> = [
  { key: 'I', label: 'I', aria: 'Chapter I — I Was Waiting' },
  { key: 'II', label: 'II', aria: 'Chapter II — The Door' },
  { key: 'III', label: 'III', aria: 'Chapter III — The Thing That Almost Broke Me' },
  { key: 'IV', label: 'IV', aria: 'Chapter IV — The In-Between' },
  { key: 'V', label: 'V', aria: 'Chapter V — The Question' },
];

export function initChapterRail(): void {
  const rail = document.createElement('nav');
  rail.className = 'chapter-rail';
  rail.setAttribute('aria-label', 'Chapter navigation');

  const buttons = new Map<ChapterKey, HTMLButtonElement>();

  for (const chapter of CHAPTERS) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'chapter-rail-num';
    btn.dataset.chapter = chapter.key;
    btn.textContent = chapter.label;
    btn.setAttribute('aria-label', chapter.aria);
    btn.addEventListener('click', () => {
      const target = document.querySelector<HTMLElement>(`section[data-chapter="${chapter.key}"]`);
      if (!target) return;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    rail.appendChild(btn);
    buttons.set(chapter.key, btn);
  }

  document.body.appendChild(rail);

  // Reveal after first paint so it fades in, matching the floating controls.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => rail.classList.add('ready'));
  });

  document.addEventListener('chapter:change', (e) => {
    const { to } = (e as CustomEvent<ChapterChangeDetail>).detail;
    buttons.forEach((btn, key) => {
      btn.classList.toggle('active', key === to);
      btn.setAttribute('aria-current', key === to ? 'true' : 'false');
    });
  });

  // Initial sync to current chapter attribute set by initChapterState().
  const current = document.documentElement.dataset.chapter as ChapterKey | undefined;
  if (current) {
    const btn = buttons.get(current);
    if (btn) {
      btn.classList.add('active');
      btn.setAttribute('aria-current', 'true');
    }
  }
}
